import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './auth/AuthProvider.js';
import { LoginPage } from './screens/auth/LoginPage.js';
import { MfaVerifyPage } from './screens/auth/MfaVerifyPage.js';
import { VendorDashboardPage } from './screens/VendorDashboardPage.js';
import { CompletionReviewPage } from './screens/CompletionReviewPage.js';
import { PhotoValidationPage } from './screens/PhotoValidationPage.js';
import { GpsValidationPage } from './screens/GpsValidationPage.js';
import { AreaValidationPage } from './screens/AreaValidationPage.js';
import { MaterialValidationPage } from './screens/MaterialValidationPage.js';
import { ReworkPage } from './screens/ReworkPage.js';
import { EvidenceRequestPage } from './screens/EvidenceRequestPage.js';
import { QualityScoringPage } from './screens/QualityScoringPage.js';
import { PainterPerformancePage } from './screens/PainterPerformancePage.js';
import { ReviewAuditPage } from './screens/ReviewAuditPage.js';
import { VendorInboxPage } from './screens/portal/VendorInboxPage.js';
import { AssignmentAcceptPage } from './screens/portal/AssignmentAcceptPage.js';
import { PainterListPage } from './screens/portal/PainterListPage.js';
import { PainterCreatePage } from './screens/portal/PainterCreatePage.js';
import { VendorOpsDashboardPage } from './screens/portal/VendorOpsDashboardPage.js';
import { VendorPaymentsPage } from './screens/portal/VendorPaymentsPage.js';
import { VendorPaymentAdvicesPage } from './screens/portal/VendorPaymentAdvicesPage.js';
import { VendorBillingInboxPage } from './screens/portal/VendorBillingInboxPage.js';
import { VendorBillingNewPage } from './screens/portal/VendorBillingNewPage.js';
import { VendorBillingDetailPage } from './screens/portal/VendorBillingDetailPage.js';
import { VendorTaxInvoicePage } from './screens/portal/VendorTaxInvoicePage.js';
import { VendorBillingClaimsPage } from './screens/portal/VendorBillingClaimsPage.js';
import { VendorShell } from './shell/VendorShell.js';

function RequireAuth({ children }: { readonly children: ReactNode }): ReactNode {
  const { user } = useAuth();
  if (!user) return <Navigate to="/auth/login" replace />;
  return <VendorShell>{children}</VendorShell>;
}

export function Router(): ReactNode {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/vendor/dashboard" replace />} />

      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/mfa" element={<MfaVerifyPage />} />

      <Route path="/vendor/dashboard" element={<RequireAuth><VendorDashboardPage /></RequireAuth>} />
      <Route path="/vendor/reviews/:id" element={<RequireAuth><CompletionReviewPage /></RequireAuth>} />
      <Route path="/vendor/reviews/:id/photos" element={<RequireAuth><PhotoValidationPage /></RequireAuth>} />
      <Route path="/vendor/reviews/:id/gps" element={<RequireAuth><GpsValidationPage /></RequireAuth>} />
      <Route path="/vendor/reviews/:id/areas" element={<RequireAuth><AreaValidationPage /></RequireAuth>} />
      <Route path="/vendor/reviews/:id/materials" element={<RequireAuth><MaterialValidationPage /></RequireAuth>} />
      <Route path="/vendor/reviews/:id/rework" element={<RequireAuth><ReworkPage /></RequireAuth>} />
      <Route path="/vendor/reviews/:id/evidence" element={<RequireAuth><EvidenceRequestPage /></RequireAuth>} />
      <Route path="/vendor/reviews/:id/score" element={<RequireAuth><QualityScoringPage /></RequireAuth>} />
      <Route path="/vendor/reviews/:id/performance" element={<RequireAuth><PainterPerformancePage /></RequireAuth>} />
      <Route path="/vendor/reviews/:id/audit" element={<RequireAuth><ReviewAuditPage /></RequireAuth>} />

      <Route path="/vendor/portal/inbox" element={<RequireAuth><VendorInboxPage /></RequireAuth>} />
      <Route path="/vendor/portal/assignment/:id" element={<RequireAuth><AssignmentAcceptPage /></RequireAuth>} />
      <Route path="/vendor/portal/painters" element={<RequireAuth><PainterListPage /></RequireAuth>} />
      <Route path="/vendor/portal/painters/new" element={<RequireAuth><PainterCreatePage /></RequireAuth>} />
      <Route path="/vendor/portal/dashboard" element={<RequireAuth><VendorOpsDashboardPage /></RequireAuth>} />
      <Route path="/vendor/portal/billing/inbox" element={<RequireAuth><VendorBillingInboxPage /></RequireAuth>} />
      <Route path="/vendor/portal/billing/new" element={<RequireAuth><VendorBillingNewPage /></RequireAuth>} />
      <Route path="/vendor/portal/billing/:invoiceId" element={<RequireAuth><VendorBillingDetailPage /></RequireAuth>} />
      <Route path="/vendor/portal/billing/claims" element={<RequireAuth><VendorBillingClaimsPage /></RequireAuth>} />
      <Route path="/vendor/portal/billing/:claimId/invoice" element={<RequireAuth><VendorTaxInvoicePage /></RequireAuth>} />
      <Route path="/vendor/portal/payments" element={<RequireAuth><VendorPaymentsPage /></RequireAuth>} />
      <Route path="/vendor/portal/payment-advices" element={<RequireAuth><VendorPaymentAdvicesPage /></RequireAuth>} />

      <Route path="*" element={<div role="alert" aria-label="not-found">Not Found</div>} />
    </Routes>
  );
}
